import React, { useRef, useState } from "react";
import Modal from "./Modal";

// Drag-and-drop or click-to-pick upload zone. POSTs to /api/admin/upload
// with the supplied `slot`; on success calls onChange(url). Renders the
// current value as a thumbnail (image) or label (video).
//
// Implementation notes:
// - Picker triggering: the <input> is rendered inside the parent <Field>
//   <label>. Clicks on any visible part of the DropZone are routed to the
//   input via the browser's native label-association — no JS click() call
//   needed, and no recursive picker because there's only one trigger path.
// - Upload progress: XMLHttpRequest (fetch lacks upload progress events).
// - Pre-upload large-file warning fires at 95 MB (just below the CF Pages
//   Functions request-body cap on the Free / Pro account plans).
// - Post-failure redirect: any 413 — or any failure on a >95 MB file — opens
//   a "go upload via R2 directly" modal that deep-links to the CF dashboard
//   for this slot's exact prefix and offers a copyable wrangler command.
// - The wrapper is non-interactive while an upload is in flight.

const LARGE_FILE_BYTES = 95 * 1024 * 1024;

// Mirrors the IMAGE_MIME / VIDEO_MIME sets in functions/api/admin/upload.ts.
// Surfaced in each DropZone so the user sees what's accepted before picking.
const IMAGE_FORMATS = "jpg · png · webp · gif";
const VIDEO_FORMATS = "mp4 · mov · webm";

// Mirrors functions/api/admin/upload.ts → resolveSlot. Kept in sync by hand.
function slotToR2Path(slot) {
  if (slot === "hero") return "hero";
  if (slot === "reel") return "reel";
  if (slot === "contact") return "contact";
  if (slot === "backgrounds.works-deeper") return "backgrounds/works-deeper";
  const parts = slot.split(".");
  if (parts[0] === "works" && parts.length >= 3) {
    const slugOk = /^[a-z0-9-]+$/.test(parts[1]);
    if (!slugOk) return null;
    if (parts[2] === "cover" && parts.length === 3) {
      return `works/${parts[1]}/cover`;
    }
    if (parts[2] === "gallery" && parts.length === 4 && /^[1-9]\d{0,2}$/.test(parts[3])) {
      return `works/${parts[1]}/gallery/${parts[3]}`;
    }
  }
  return null;
}

// Per project bindings (see wrangler.toml). Hardcoded so a failure modal can
// produce a direct dashboard link without an extra API roundtrip.
const CF_ACCOUNT_ID = "e0eb494cc2ab5c9387e9ec4440dd1612";
const R2_BUCKET = "rubys-website-media";

function r2DashboardUrl() {
  return `https://dash.cloudflare.com/${CF_ACCOUNT_ID}/r2/default/buckets/${R2_BUCKET}`;
}

function fmtMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function wranglerCommand(prefix, filename) {
  const safeName = (filename || "upload").replace(/[^a-zA-Z0-9._-]/g, "_");
  return `wrangler r2 object put ${R2_BUCKET}/${prefix}/$(date +%s)-${safeName} --file=./${safeName} --remote`;
}

export default function DropZone({ slot, value, kind = "image", onChange }) {
  const inputRef = useRef(null);
  const xhrRef = useRef(null);
  const [progress, setProgress] = useState(null); // null | "uploading" | string
  const [percent, setPercent] = useState(0);
  const [hover, setHover] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // pre-upload size warning
  const [failure, setFailure] = useState(null); // { status, message, file }
  const [copied, setCopied] = useState(false);

  const uploading = progress === "uploading";
  const r2Prefix = slotToR2Path(slot);

  function reallyUpload(file) {
    if (!file) return;
    setProgress("uploading");
    setPercent(0);

    const fd = new FormData();
    fd.append("slot", slot);
    fd.append("file", file);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;
    xhr.open("POST", "/api/admin/upload");
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setPercent(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      xhrRef.current = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          onChange(data.url || data.mediaUrl);
          setProgress(null);
          setPercent(0);
        } catch {
          setProgress("error: invalid server response");
        }
      } else {
        const message = xhr.responseText || xhr.statusText || "unknown";
        setProgress(`error: ${xhr.status} ${message}`);
        // 413 — or any failure on a file already in the size danger zone —
        // surfaces the redirect modal so the user has a clear next step.
        if (xhr.status === 413 || file.size > LARGE_FILE_BYTES) {
          setFailure({ status: xhr.status, message, file });
        }
      }
    };
    xhr.onerror = () => {
      xhrRef.current = null;
      setProgress("error: network");
      // Network-mid-upload often means CF closed the connection because the
      // body crossed the platform cap. Surface the redirect for big files.
      if (file.size > LARGE_FILE_BYTES) {
        setFailure({ status: 0, message: "network (likely platform-cap rejection)", file });
      }
    };
    xhr.onabort = () => {
      xhrRef.current = null;
      setProgress(null);
      setPercent(0);
    };
    xhr.send(fd);
  }

  function handleFile(file) {
    if (!file || uploading) return;
    if (file.size > LARGE_FILE_BYTES) {
      setPendingFile(file);
      return;
    }
    reallyUpload(file);
  }

  async function copyWranglerCmd() {
    if (!failure || !r2Prefix) return;
    const cmd = wranglerCommand(r2Prefix, failure.file?.name);
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Best effort; clipboard may be blocked. The command is visible in the
      // modal regardless, so the user can copy by hand.
    }
  }

  return (
    <>
      <div
        onDrop={(e) => {
          e.preventDefault();
          setHover(false);
          if (uploading) return;
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!uploading) setHover(true);
        }}
        onDragLeave={() => setHover(false)}
        style={{
          border: `1px dashed ${hover ? "#f4ece0" : "#3a3a3a"}`,
          background: hover ? "#1a1a1a" : "#0f0f0f",
          borderRadius: 4,
          padding: "0.7rem",
          display: "flex",
          gap: "0.7rem",
          alignItems: "center",
          cursor: uploading ? "default" : "pointer",
          opacity: uploading ? 0.85 : 1,
          transition: "background 120ms",
          position: "relative",
          overflow: "hidden",
          pointerEvents: uploading ? "none" : "auto",
        }}
      >
        <div
          style={{
            width: 64,
            height: 40,
            background: "#222",
            borderRadius: 2,
            overflow: "hidden",
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            color: "#666",
            fontSize: "0.7rem",
          }}
        >
          {kind === "image" && value ? (
            <img
              src={value}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            kind
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: "#f4ece0",
              fontSize: "0.78rem",
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            {uploading
              ? `uploading… ${percent}%`
              : progress?.startsWith("error")
              ? progress
              : `${kind} · drop or click · ${kind === "video" ? VIDEO_FORMATS : IMAGE_FORMATS}`}
          </div>
          <div
            style={{
              color: "#666",
              fontSize: "0.7rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={value}
          >
            {value || "(none)"}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={kind === "video" ? "video/*" : "image/*"}
          style={{ display: "none" }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const f = e.target.files?.[0];
            // Reset so the same file can be re-selected after an error.
            e.target.value = "";
            handleFile(f);
          }}
        />
        {uploading && (
          <div
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              height: 2,
              width: `${percent}%`,
              background: "#f4ece0",
              transition: "width 120ms",
            }}
          />
        )}
      </div>

      {/* Pre-upload size warning */}
      <Modal
        open={!!pendingFile}
        title="Use wrangler instead"
        confirmLabel="Try anyway"
        cancelLabel="Cancel"
        destructive
        onCancel={() => setPendingFile(null)}
        onConfirm={() => {
          const f = pendingFile;
          setPendingFile(null);
          reallyUpload(f);
        }}
      >
        <p style={{ margin: 0, marginBottom: "0.7rem" }}>
          <strong>{pendingFile?.name}</strong> is{" "}
          <strong>{pendingFile ? fmtMB(pendingFile.size) : ""}</strong> — close
          to or above the Cloudflare Pages Functions{" "}
          <strong>100&nbsp;MB</strong> request-body cap on the Free plan.
          Uploading through this editor will likely fail at the platform edge
          with a 413 before reaching the site's code.
        </p>
        <p style={{ margin: 0, marginBottom: "0.5rem" }}>
          For files this size — typically video — upload directly to R2 with{" "}
          <code style={{ fontSize: "0.85em" }}>wrangler r2 object put</code>{" "}
          (multipart, up to ~5&nbsp;TiB). The public resolver picks the newest
          file in each slot folder automatically and the redirect cache TTL is
          60 s, so no doc edit or redeploy is needed.
        </p>
        <p style={{ margin: 0, color: "#888" }}>
          Existing R2 files remain untouched if you cancel here.
        </p>
      </Modal>

      {/* Post-failure redirect */}
      <Modal
        open={!!failure}
        title={
          failure?.status === 413
            ? "Upload too large for the editor"
            : "Upload failed — try direct R2 upload"
        }
        confirmLabel="Open R2 in CF dashboard ↗"
        cancelLabel="Close"
        onCancel={() => {
          setFailure(null);
          setCopied(false);
        }}
        onConfirm={() => {
          window.open(r2DashboardUrl(), "_blank", "noopener,noreferrer");
        }}
      >
        <p style={{ margin: 0, marginBottom: "0.7rem" }}>
          The upload was rejected
          {failure?.status ? ` (HTTP ${failure.status})` : ""}
          . Most likely cause: the file{" "}
          {failure?.file ? (
            <>
              (<strong>{fmtMB(failure.file.size)}</strong>)
            </>
          ) : null}{" "}
          exceeded the 100&nbsp;MB Cloudflare Pages Functions request-body cap
          on the Free plan.
        </p>
        <p style={{ margin: 0, marginBottom: "0.5rem" }}>
          Upload it straight to R2 instead. The "Open R2 in CF dashboard"
          button takes you to the bucket — drill into{" "}
          <strong>this slot's folder</strong>:
        </p>
        <pre
          style={{
            margin: 0,
            marginBottom: "0.7rem",
            padding: "0.45rem 0.6rem",
            background: "#1a1a1a",
            border: "1px solid #2a2a2a",
            borderRadius: 3,
            fontSize: "0.78rem",
            color: "#f4ece0",
            overflowX: "auto",
          }}
        >
          {R2_BUCKET}/{r2Prefix || "<unknown-slot>"}/
        </pre>
        <p style={{ margin: 0, marginBottom: "0.4rem", color: "#888" }}>
          Or via wrangler:
        </p>
        <div
          style={{
            display: "flex",
            gap: "0.4rem",
            alignItems: "stretch",
            marginBottom: "0.6rem",
          }}
        >
          <pre
            style={{
              margin: 0,
              flex: 1,
              padding: "0.45rem 0.6rem",
              background: "#1a1a1a",
              border: "1px solid #2a2a2a",
              borderRadius: 3,
              fontSize: "0.72rem",
              color: "#cfcabd",
              overflowX: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
            }}
          >
            {r2Prefix
              ? wranglerCommand(r2Prefix, failure?.file?.name)
              : "(unknown slot — see docs/admin.md)"}
          </pre>
          {r2Prefix && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyWranglerCmd();
              }}
              style={{
                background: copied ? "#2e4a2e" : "transparent",
                color: "#f4ece0",
                border: "1px solid #2a2a2a",
                padding: "0 0.7rem",
                fontSize: "0.75rem",
                borderRadius: 3,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {copied ? "copied" : "copy"}
            </button>
          )}
        </div>
        <p style={{ margin: 0, color: "#888", fontSize: "0.78rem" }}>
          Run <code style={{ fontSize: "0.9em" }}>source .cf-env</code> first.
          Existing R2 files are untouched — the upload didn't reach this
          site's code.
        </p>
      </Modal>
    </>
  );
}
